import { create, type UseBoundStore, type StoreApi } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { HilEvent } from '../types/hil.types.js';

export interface HilStoreState {
  pendingEvents: HilEvent[];
  activeEvent: HilEvent | null;
  overlayOpen: boolean;
  submitting: boolean;
  submitError: string | null;

  pushHilEvent: (event: HilEvent) => void;
  resolveHilEvent: (eventId: string) => void;
  setSubmitting: (submitting: boolean) => void;
  setSubmitError: (error: string | null) => void;
  closeOverlay: () => void;
}

export const useHilStore: UseBoundStore<StoreApi<HilStoreState>> = create<HilStoreState>()(
  devtools(
    immer((set, get) => ({
      pendingEvents: [],
      activeEvent: null,
      overlayOpen: false,
      submitting: false,
      submitError: null,

      pushHilEvent: (event) => set((state) => {
        if (!state.overlayOpen || !state.activeEvent) {
          state.activeEvent = event;
          state.overlayOpen = true;
          state.submitError = null;
        } else {
          state.pendingEvents.push(event);
        }
      }),

      resolveHilEvent: (eventId) => set((state) => {
        if (state.activeEvent?.id === eventId) {
          if (state.pendingEvents.length > 0) {
            state.activeEvent = state.pendingEvents.shift()!;
            state.submitting = false;
            state.submitError = null;
          } else {
            state.activeEvent = null;
            state.pendingEvents = [];
            state.overlayOpen = false;
            state.submitting = false;
            state.submitError = null;
          }
        } else {
          state.pendingEvents = state.pendingEvents.filter((e) => e.id !== eventId);
        }
      }),

      setSubmitting: (submitting) => set((state) => { state.submitting = submitting; }),

      setSubmitError: (submitError) => set((state) => { state.submitError = submitError; }),

      closeOverlay: () => set((state) => { state.overlayOpen = false; }),
    })),
    { name: 'HilStore' }
  )
);
